import type { CallCategory, SalesChecklistBehaviors } from "@expert/shared-types";

import { DEAD_AIR_TAG, addMessage, setupOrchestratorTimeout } from "@asurion-core/expert-workspace-timeline";
import { gaiaWsEventBus } from "@expert/gaia";
import { getLogger } from "@expert/logging";
import { triggerProactiveSound, triggerTabNotification } from "@expert/proactive-notifications";

const logger = getLogger({ module: "GaiaTab" });

export const emitProactiveGaiaEvent = (
    partner: string,
    sessionId: string,
    suggestionOverrides: Record<string, unknown>,
) => {
    const timestamp = Date.now();
    gaiaWsEventBus.emit("gaia_ws_message-suggestion", {
        name: "message-suggestion",
        correlationId: `dev-tool-${timestamp}`,
        timestamp,
        data: undefined,
        body: {
            partner,
            sessionGroupId: sessionId,
            sessionId,
            suggestion: {
                message: "",
                messageId: `dev-${timestamp}`,
                messageType: "Proactive",
                producer: "orchestrator",
                outputTimestamp: new Date().toISOString(),
                ...suggestionOverrides,
            },
        },
    });
};

export const emitLocationCues = (
    partner: string,
    sessionId: string,
    cityVal: string,
    stateVal: string,
    setCity: (s: string) => void,
    setState: (s: string) => void,
) => {
    const cues = [
        { name: stateVal, type: "LOCATION_STATE" as const },
        { name: cityVal, type: "LOCATION_CITY" as const },
    ].filter((cue) => cue.name);
    if (cues.length === 0) return;
    emitProactiveGaiaEvent(partner, sessionId, {
        cues,
        metadata: { intentClassification: "location-detection", applicationIdentifier: "location-cues" },
    });
    setCity("");
    setState("");
};

export const emitDeviceCue = (
    partner: string,
    sessionId: string,
    device: string,
    setDeviceName: (s: string) => void,
) => {
    if (!device) return;
    emitProactiveGaiaEvent(partner, sessionId, { cues: [{ name: device, count: 1, type: "DEVICE" }] });
    setDeviceName("");
};

export const emitSalesChecklistBehavior = (partner: string, sessionId: string, behavior: SalesChecklistBehaviors) => {
    emitProactiveGaiaEvent(partner, sessionId, {
        responseType: "sales-checklist-mentions",
        sales_checklist_mentions: [{ name: behavior, value: true }],
    });
};

export const emitGuideRecommendation = (partner: string, sessionId: string, callCategory: CallCategory) => {
    emitProactiveGaiaEvent(partner, sessionId, {
        category: "proactive-notification",
        cues: [{ name: callCategory, type: "CALL_CATEGORY" }],
    });
};

// Mirrors the real dead-air producer (apps/expert-ui/src/dead-air/useDeadAirAssist.ts): a
// LOOKUP_STALL classification where the expert went quiet trying to find plan info the customer
// asked about, surfaced directly as a tagged Proactive message with the notification cues every
// other Proactive message gets.
export const emitDeadAirLookupStall = (sessionId: string) => {
    addMessage(sessionId, {
        id: `deadAir_${crypto.randomUUID()}`,
        text: "The customer's plan is Total Mobile Protection for Business — it covers same-day device replacement, extended warranty, and AppleCare integration for eligible iPhones.",
        type: "Proactive",
        tag: DEAD_AIR_TAG,
        isUnread: true,
        status: "active",
        timestamp: new Date().toISOString(),
    });
    triggerTabNotification("Expert Workspace", true);
    triggerProactiveSound(true);
};

const DEAD_AIR_PROCEDURAL_FIX_GUIDANCE = `**Activate the replacement iPhone via eSIM transfer**

1. Open **Settings > Cellular** on the new iPhone and tap **Add eSIM**, then choose **Transfer From Nearby iPhone**.
2. Hold the old and new iPhones close together until the transfer code appears, then confirm the transfer on the old device.
3. Wait for **Setting Up Mobile Service** to finish, then tap **Settings > Cellular > Cellular Plans** and confirm the new eSIM is set as the primary line.
4. Restart the new iPhone and confirm carrier name and signal bars appear in the status bar.
5. Tap **Settings > General > About** and confirm the IMEI listed matches the replacement device on the claim.
6. Tap **Settings > Cellular > Cellular Plans**, select the old line, and tap **Remove Cellular Plan** to release it.`;

// Mirrors the real dead-air producer (apps/expert-ui/src/dead-air/useDeadAirAssist.ts): a
// LOOKUP_STALL classification where the expert went quiet looking up the steps to walk the
// customer through a device-replacement activation, surfaced as a tagged Proactive message with
// the notification cues every other Proactive message gets. Exercises the PROCEDURAL answer
// shape (numbered markdown steps with real menu/setting names) the rewritten prompt targets, and
// is long enough to trip the timeline's 9.5625rem text mask so the "Show more" expand affordance
// has a manual repro.
export const emitDeadAirProceduralFix = (sessionId: string) => {
    addMessage(sessionId, {
        id: `deadAir_${crypto.randomUUID()}`,
        text: DEAD_AIR_PROCEDURAL_FIX_GUIDANCE,
        type: "Proactive",
        tag: DEAD_AIR_TAG,
        isUnread: true,
        status: "active",
        timestamp: new Date().toISOString(),
    });
    triggerTabNotification("Expert Workspace", true);
    triggerProactiveSound(true);
};

const APPLE_ID_RESET_GUIDANCE = `## Reset Apple ID password

1. Open Settings
2. Tap their name
3. Tap Sign-In & Security
4. Tap Change Password`;

const STORAGE_GUIDANCE = `## Check storage on an older Samsung

1. Open Settings
2. Tap Battery and device care
3. Tap Storage
4. Review used and available space`;

export const emitProactiveGuidance = (
    partner: string,
    sessionId: string,
    variant: "apple-id-reset" | "with-quick-actions" | "suppressed",
) => {
    const isSuppressed = variant === "suppressed";
    const hasQuickActions = variant === "with-quick-actions";

    emitProactiveGaiaEvent(partner, sessionId, {
        producer: "proactive-guidance",
        message: hasQuickActions ? STORAGE_GUIDANCE : APPLE_ID_RESET_GUIDANCE,
        metadata: {
            intentClassification: "proactive-guidance",
            suppressionFlag: isSuppressed,
            suppressionReason: isSuppressed ? "Dev tool suppressed guidance" : undefined,
        },
        quickActions: hasQuickActions
            ? [
                  {
                      label: "Walk me through this",
                      prompt: "Walk me through checking storage on an older Samsung phone",
                      type: "SendAssistMessage",
                  },
                  {
                      label: "Why is storage full?",
                      prompt: "Why might storage be full on an older Samsung phone?",
                      type: "SendAssistMessage",
                  },
              ]
            : undefined,
    });
};

export const simulateSilentTimeout = (sessionId: string) => {
    const correlationId = `dev-tool-timeout-${crypto.randomUUID()}`;
    const messageId = `dev-timeout-${crypto.randomUUID()}`;
    const messageText = "[Dev Tool] Simulated expert message (no GAIA response will arrive)";
    addMessage(sessionId, {
        id: messageId,
        text: messageText,
        type: "Expert",
        isUnread: false,
        status: "active",
    });
    setupOrchestratorTimeout(correlationId, sessionId, logger, messageId, {
        text: messageText,
        type: "Expert",
        source: "DevTool",
    });
};

export const simulateOrchestratorError = (sessionId: string) => {
    const timestamp = Date.now();
    const messageId = `dev-${timestamp}`;
    const messageText = "Customer is having issues with their device not turning on";
    addMessage(sessionId, {
        id: messageId,
        text: messageText,
        type: "Expert",
        isUnread: false,
        status: "active",
    });
    setTimeout(() => {
        gaiaWsEventBus.emit("gaia_ws_orchestrator-invoke-error", {
            name: "orchestrator-invoke-error",
            correlationId: `dev-tool-${timestamp}`,
            timestamp,
            data: undefined,
            body: {
                message: "Failed to invoke orchestrator",
                status: 500,
                statusText: "Internal Server Error",
                trackingId: `dev-tool-tracking-${timestamp}`,
                responseText: "Internal server error",
                body: JSON.stringify({
                    sessionId,
                    sessionGroupId: sessionId,
                    message: messageText,
                    userId: "dev-tool",
                    messageId,
                    messageType: "Search",
                    userType: "expert",
                }),
            },
        });
    }, 1500);
};
